import React, { useState } from 'react';

import {
  DISCHARGE_TYPE, isTerminal, dodIsConfirmed, snippetLimitReachedCopy,
} from '../../../constants/admission';
import {
  formatDateTimeIST, utcIsoToISTInputValue, istInputValueToUtcIso, computeLengthOfStay,
} from '../../../utils/formatIST';
import { validatePlanDischarge, planDischargeErrorMessage, splitISTValue } from './planDischargeModel';
import { DISCHARGE_DATE_CONFIRMED_NOTE } from './dischargeModel';
import InvestigationRowsField, {
  INVESTIGATION_ROWS_MAX,
  INVESTIGATION_RESULT_MAX_LENGTH,
  INVESTIGATION_NAME_MAX_LENGTH_DEFAULT,
} from './InvestigationRowsField';
import BulletLinesField from './BulletLinesField';
import SnippetPicker from './SnippetPicker';
import SaveAsTemplateModal from './SaveAsTemplateModal';
import ComplaintItemsField from './ComplaintItemsField';

const labelCls = 'block text-xs font-medium text-slate-500 dark:text-slate-400 mb-1';
const inputCls = 'w-full px-2.5 py-2 rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-900 text-slate-800 dark:text-slate-100 text-sm';
const helperCls = 'text-[11px] text-slate-500 dark:text-slate-400 mt-1';
const readonlyValueCls = 'text-[13px] text-slate-800 dark:text-slate-100';
// verdict-r2.md ruling C-3 — same red-text convention `BulletLinesField.jsx`'s
// own `errorCls` already uses; reused here so a field-level 422 reads
// identically regardless of which leaf widget renders it.
const errorCls = 'text-[11.5px] text-red-600 dark:text-red-400 mt-1';

// UX v2 spec (doctor-summary-form-v2-SPEC.md §2) — narrative fields only,
// `labelCls` above stays untouched for `HeaderField` (owner said the header
// block is fine).
const narrativeLabelCls = 'flex items-center gap-1 text-[13px] font-semibold text-slate-700 dark:text-slate-200 mb-1.5';
// DISCHARGE-TEMPLATES 1b Slice 1 (FE-6) — the SAME label look, but without
// `narrativeLabelCls`'s own bottom margin, for the row that also carries the
// "Use template"/"Save as template" toolbar (the WRAPPING row owns the
// margin instead, so the label and the toolbar sit on one baseline).
const narrativeLabelInlineCls = 'flex items-center gap-1 text-[13px] font-semibold text-slate-700 dark:text-slate-200';
// Dormant today (§2 — Phase 1's catalogue carries no per-field required flag)
// but styled now so Phase 2 needs no second pass; matches the existing
// "Reason (required)"/"Authorised by (required)" convention in DischargeDialog.jsx.
const requiredSuffixCls = 'text-[11px] font-semibold text-slate-500 dark:text-slate-400';

// UX v2 spec §3 — narrative textarea base classes (focus ring added; every
// other narrative textarea class here, incl. the leaf widgets' own inputs,
// is UNCHANGED).
const narrativeTextareaCls = 'w-full px-3 py-2.5 rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-900 text-slate-800 dark:text-slate-100 text-[13.5px] leading-relaxed focus:outline-none focus:ring-2 focus:ring-teal-500/60 focus:border-teal-500 dark:focus:ring-teal-400/40 dark:focus:border-teal-400';

// UX v2 spec §3 — per-field-kind rows/min-height, keyed by canonical key.
// `default`/unknown key -> `medium`, so a forward-compat kind still gets a
// sane size instead of the old cramped `rows=3` default.
// INTEGRATION FIX (round-2 fold) — these are CANONICAL KEYS as the server
// sends them (`app/core/discharge_field_catalogue.py`), NOT the owner's
// printed labels. FIX-4-FE keyed this map and `NARRATIVE_GROUPS` off
// label-shaped names no envelope has ever carried, so 5 of the 10 narrative
// fields silently fell back to the default size and to NO group caption.
// Guarded by a test that every key named here is a key the server sends.
// DISCHARGE-TEMPLATES 1b Slice 1 (FE-6) — `follow_up` is now a `value_type:
// "date"` field (renders `<input type="date">`, own sizing) — its old
// textarea-rows entry is dead and REMOVED (build-plan-1b-slice1.md FE-6 (e)).
// `instructions` is the new order-15 narrative key, same size treatment as
// `condition_at_discharge` (its "At discharge" group sibling).
export const NARRATIVE_SIZE = {
  chief_complaints: { rows: 3, cls: 'min-h-[84px]' },
  physical_examination: { rows: 3, cls: 'min-h-[84px]' },
  condition_at_discharge: { rows: 3, cls: 'min-h-[84px]' },
  instructions: { rows: 3, cls: 'min-h-[84px]' },
  diagnosis: { rows: 2, cls: 'min-h-[56px]' },
  diagnosis_final: { rows: 2, cls: 'min-h-[56px]' },
  ot_note: { rows: 5, cls: 'min-h-[132px]' },
  hospital_course: { rows: 5, cls: 'min-h-[132px]' },
};
const NARRATIVE_SIZE_DEFAULT = { rows: 4, cls: 'min-h-[104px]' };

// UX v2 spec §1 — field-key -> phase-group caption, walked in the server's
// own `narrativeFields` order (never resorts). A key in no list, or a group
// with 0 present fields, prints no caption for that group — forward
// -compatible with a future canonical key or a template hiding a whole group.
export const NARRATIVE_GROUPS = [
  {
    key: 'on_admission',
    label: 'On admission',
    hint: 'What the patient presented with and the admission work-up.',
    fieldKeys: ['chief_complaints', 'physical_examination', 'investigations', 'diagnosis'],
  },
  {
    key: 'during_stay',
    label: 'During stay',
    hint: 'Confirmed diagnosis, procedure and how the admission progressed.',
    fieldKeys: ['diagnosis_final', 'ot_note', 'hospital_course'],
  },
  {
    key: 'at_discharge',
    label: 'At discharge',
    hint: 'Medicines, condition and follow-up instructions handed to the patient.',
    // DISCHARGE-TEMPLATES 1b Slice 1 (FE-6) — `instructions` (catalogue order
    // 15) added; `follow_up` (order 16) stays last. Membership only —
    // ACTUAL render order still walks the server's `fields[]` order, never
    // this list's order (see `SummaryFields`'s own JSDoc).
    fieldKeys: ['discharge_medications', 'condition_at_discharge', 'instructions', 'follow_up'],
  },
];

function narrativeGroupCaption(group) {
  return (
    <div
      key={group.key}
      className="pt-[18px] pb-2 mt-0.5 border-t border-slate-200 dark:border-slate-700 first:border-t-0 first:pt-0 first:mt-0"
      data-testid={`summary-group-${group.key}`}
    >
      <h3 className="text-[11px] font-bold uppercase tracking-wide text-teal-700 dark:text-teal-300">{group.label}</h3>
      <p className="text-[11px] text-slate-400 dark:text-slate-500 mt-0.5">{group.hint}</p>
    </div>
  );
}

export const EMPTY_CAUSE_OF_DEATH = { immediate_cause: '', antecedent_cause: '', death_at: '' };

// Kind emitted by GET/PUT .../discharge-summary `fields[]` (frozen contract §2.1/§2.2).
// FE-2 renders every OTHER kind (incl. any the FE has never heard of) as a plain
// long-text box — forward-compatible with Phase 2 per §2.7 ("unknown kind or
// value_type must render as a plain long-text box, never crash").
const HEADER_AUTOFILL_KIND = 'header_autofill';

// R.1/R.2 (build-plan-phase1.md, DISCHARGE-TEMPLATES round-1 rework, FE-C2) —
// the `investigations` key's ONLY kind change: `narrative` -> `lab_lines`.
// Nothing else in the catalogue moves; every other kind keeps today's plain
// textarea (incl. any kind the FE has never heard of, §2.7 forward-compat).
// Exported so `DischargeSummaryPanel.jsx`'s `buildEditableValues` can guard
// the SAME kind check, single source, rather than a second inline literal.
export const LAB_LINES_KIND = 'lab_lines';

// Ratified copy (build-plan-phase1.md §4 FE-3) — verbatim, the owner's
// mitigation for the header-override slice, not decoration.
const DIFFERS_FROM_ADMISSION_COPY = 'Differs from the admission record';

// Ratified copy — owner ruling I (discharge-templates-phase1-2026-09-13.md,
// DOD-WARN-FE): an empty Date of discharge is a WARNING, never an error —
// it must never block Save or Print. Exported so a test (or a future
// caller) reads the SAME string this component renders, not a re-typed
// copy of it.
export const DOD_EMPTY_WARNING_COPY = 'Date of discharge is empty. Fill it in before printing the final summary.';
// Day-mode contrast rule (CLAUDE.md) — solid amber tint + explicit dark pair.
const dodWarningCls = 'mt-1.5 flex items-start gap-1.5 rounded-md border border-amber-400 dark:border-amber-700 bg-amber-50 dark:bg-amber-900/20 text-amber-900 dark:text-amber-200 text-[11px] px-2 py-1.5';

function DodEmptyWarningIcon() {
  return (
    <svg
      aria-hidden="true"
      viewBox="0 0 20 20"
      fill="currentColor"
      className="w-3.5 h-3.5 mt-px shrink-0 text-amber-600 dark:text-amber-400"
    >
      <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l6.28 11.164c.75 1.333-.213 2.987-1.743 2.987H3.72c-1.53 0-2.493-1.654-1.743-2.987L8.257 3.1zM10 7a.75.75 0 01.75.75v3.5a.75.75 0 01-1.5 0v-3.5A.75.75 0 0110 7zm0 8a1 1 0 100-2 1 1 0 000 2z" clipRule="evenodd" />
    </svg>
  );
}

// Client-side mirror of the server's 422 INVALID_DATE_RANGE (§2.7): the
// effective discharge datetime must not precede the effective admission
// datetime. One assert, same rule, so the doctor sees it before the PUT
// round-trip rejects it.
const DATE_RANGE_ERROR_COPY = 'Date of discharge cannot be before the date of admission.';

// Owner decision addendum ruling B (discharge-templates-phase1-2026-09-11.md,
// 2026-09-12 addendum) — "Treatment to be given" (`discharge_medications`)
// stays a free-text box but renders one bullet PER LINE. FIX-2 (owner's
// live-test bug report) replaced the original bulleted-textarea approach
// (glyph injected into the textarea's own displayed value, stripped on
// change) with `BulletLinesField` — the bullet is a static cell beside a
// dedicated single-line `<input>` per line, never part of the text, so
// there is nothing for a re-render to fight the caret over. The STORED
// value is unchanged from ruling B's contract: plain lines, no glyph, blank
// lines dropped, `null` when empty.
const BULLETED_NARRATIVE_KEY = 'discharge_medications';

// Decision addendum ruling F (discharge-templates-phase1-2026-09-11.md,
// 2026-09-13) — the new header key. Pulled out of the generic
// `gridHeaderFields` map (same treatment `doctor_signature` already gets)
// and re-inserted immediately after `discharged_at` so it always prints
// "under the discharge date" regardless of the catalogue's own field order.
export const LENGTH_OF_STAY_KEY = 'length_of_stay';

// Ruling F — "Hospital course" (`hospital_course`) stays the clinical
// narrative; this helper text is the owner's clarification that the LENGTH
// OF STAY itself is NOT typed here, it auto-fills in the header above.
const HOSPITAL_COURSE_KEY = 'hospital_course';
export const HOSPITAL_COURSE_HELPER_COPY = 'Course during the stay — progress, complications, response to treatment. The length of stay is filled in automatically above.';

// Ratified copy (ruling F) — the affordance that clears a `length_of_stay`
// override and resumes the live client-side recompute.
const RESET_TO_AUTO_COPY = 'Reset to auto';

/**
 * The single source of truth for "what is this header field's value RIGHT
 * NOW" — reused by the input's displayed value, the differs badge and the
 * date-range check, so the three can never drift from each other.
 *
 * `value[field.key]` (if the parent has ever called `onChange` for this key,
 * including an explicit clear -> null) wins over the field's own saved
 * `field.value`; an empty override (null/''/undefined) means "no override in
 * effect" and the caller falls back to `field.prefill` per §2.2's binding
 * display rule (`value ?? prefill`).
 */
function headerOverride(field, value) {
  const raw = Object.prototype.hasOwnProperty.call(value, field.key) ? value[field.key] : field.value;
  return (raw === undefined || raw === null || raw === '') ? null : raw;
}

function headerWireValue(field, value) {
  const override = headerOverride(field, value);
  return override !== null ? override : (field.prefill || null);
}

function headerDiffers(field, value) {
  const override = headerOverride(field, value);
  return override !== null && override !== field.prefill;
}

/**
 * One header row (`kind: "header_autofill"`, §2.1) — `patient_name`,
 * `ip_number`, `admitted_at`, `discharged_at`, `mlc_no`, `doctor_signature`.
 * `editable:false` renders read-only (same treatment as `NarrativeField`).
 * `value_type:"datetime"` renders `<input type="datetime-local">`, its wall
 * -clock value shown/edited in IST via the two `formatIST` helpers — the
 * UTC ISO wire value never touches `new Date(naiveString)` (bk-646 lesson,
 * §1a). `value_type:"text"` (or anything else, forward-compat §2.7) renders
 * a text input. MLC (`mlc_no`) is always rendered here regardless of any
 * `is_mlc` flag on the admission — the server always emits it (§1b), this
 * component adds no gate of its own.
 *
 * `discharged_at` is NEVER dispatched here any more (FIX-7-FE, owner ruling
 * reversing lite-pr S4 AC5) — `SummaryFields` routes that ONE key to
 * `DischargeDateField` instead (own JSDoc below).
 */
function HeaderField({
  field, value, onChange, error, dateLocked = false,
}) {
  const { key, label, value_type: valueType } = field;
  // Owner ruling: once discharge is initiated every DATE field is read-only.
  const editable = field.editable && !(dateLocked && valueType === 'datetime');
  const inputId = `sf-${key}`;
  const wireValue = headerWireValue(field, value);
  const differs = headerDiffers(field, value);
  // verdict-r2.md ruling C-3 — a field-named 422 (e.g. FIELD_TOO_LONG on a
  // capped header like `mlc_no`); read-only headers can never 422, so no
  // guard is needed there.
  const errorLine = error && error.message && (
    <div className={errorCls} data-testid={`summary-field-${key}-error`}>{error.message}</div>
  );

  if (!editable) {
    const display = valueType === 'datetime' ? formatDateTimeIST(wireValue) : wireValue;
    return (
      <div>
        <label className={labelCls}>{label}</label>
        <div className={readonlyValueCls} data-testid={`summary-field-${key}`}>{display || '—'}</div>
      </div>
    );
  }

  const differsBadge = differs && (
    <div className={helperCls} data-testid={`summary-field-${key}-differs`}>{DIFFERS_FROM_ADMISSION_COPY}</div>
  );

  if (valueType === 'datetime') {
    return (
      <div>
        <label className={labelCls} htmlFor={inputId}>{label}</label>
        <input
          id={inputId}
          type="datetime-local"
          className={inputCls}
          value={utcIsoToISTInputValue(wireValue)}
          onChange={(e) => {
            const naive = e.target.value;
            onChange(key, naive ? istInputValueToUtcIso(naive) : null);
          }}
          data-testid={`summary-field-${key}`}
        />
        {differsBadge}
        {errorLine}
      </div>
    );
  }

  return (
    <div>
      <label className={labelCls} htmlFor={inputId}>{label}</label>
      <input
        id={inputId}
        type="text"
        className={inputCls}
        maxLength={field.max_length || undefined}
        value={wireValue || ''}
        onChange={(e) => {
          const next = e.target.value;
          onChange(key, next === '' ? null : next);
        }}
        data-testid={`summary-field-${key}`}
      />
      {differsBadge}
      {errorLine}
    </div>
  );
}

// FIX-7-FE (owner ruling 2026-09-19, "summary Date of discharge vs plan",
// option 2 — REVERSES lite-pr S4 AC5's "the existing editable header
// override still wins"). Ratified copy, verbatim.
export const DISCHARGE_DATE_PLANNED_LABEL = 'Date of discharge (planned)';
export const DISCHARGE_DATE_RECORDED_NOTE = 'Recorded at discharge. A correction is issued as a corrected copy.';
// DOD rule v3 / S5 AC4 + the ONE-DOOR ruling (orchestrator, BE-1 return):
// after Initiate this field MIRRORS the confirmed date read-only — the single
// door for changing it is the admission's own "Change discharge date", so the
// editor can never become a second, guard-free setter.
export const DISCHARGE_DATE_CONFIRMED_LABEL = 'Date of discharge (confirmed)';


/**
 * `discharged_at` header row — REPLACES `HeaderField` for this ONE key
 * (every other header key is untouched). One source for the discharge
 * date: pre-completion it edits the discharge PLAN, the exact same value
 * `PlanDischargeDialog` (AdmissionWorkspace), the desk row chip and the
 * running-bill chip already read/write — never a second, independent
 * print-time override.
 *
 * `completed` (`isTerminal(admission.status)`, computed once by
 * `SummaryFields` below) decides the branch:
 *  - `false` (open stay): editable, bound to `admission.planned_discharge_at`
 *    — NEVER `field.value`/`value[key]`. This component never calls the
 *    parent `onChange`, so `discharged_at` can never ride the summary's own
 *    `{values}` PUT again (§2.3) — `DischargeSummaryPanel.buildEditableValues`
 *    also excludes the key outright, belt-and-braces. Validated with the
 *    EXACT SAME pure predicate `PlanDischargeDialog` uses
 *    (`validatePlanDischarge`, `planDischargeModel.js` — reused, never
 *    duplicated): "before admission" / "more than 7 days ahead", `Save`
 *    disabled while invalid. `Save`/`Clear` call `onSavePlan` (DI — this
 *    component never imports the API itself, the SAME convention
 *    `PlanDischargeDialog.onSave` already sets); a successful save's new
 *    plan flows back down through the `admission` prop (the hosting panel's
 *    own local-override merge, the identical pattern `AdmissionWorkspace`'s
 *    `plannedDischargeOverride` already uses) — no second GET. `canEdit`
 *    (ADMIT_PATIENT OR DISCHARGE_PATIENT, the SAME OR-gate the Plan dialog's
 *    own trigger uses) renders a plain read-only mirror, no edit affordance,
 *    when false (e.g. a nurse).
 *  - `true` (completed): READ-ONLY, the UNCHANGED single-source resolver
 *    (`headerWireValue`) — a legacy stored override on an already-completed
 *    stay (older data, pre-dating this ruling) still prints verbatim, never
 *    stripped — plus the ratified correction-rail note. A signed clinical
 *    record; corrected only via a re-issued, re-signed copy through the
 *    existing correction rail, never a silent edit here.
 */
function DischargeDateField({
  field, value, admission, completed, confirmed, canEdit, onSavePlan,
}) {
  const { key, label } = field;
  const inputId = `sf-${key}`;
  const admittedAt = admission && admission.admitted_at;
  const plannedIso = (admission && admission.planned_discharge_at) || null;

  const [draft, setDraft] = useState(() => utcIsoToISTInputValue(plannedIso));
  const [busy, setBusy] = useState(false);
  const [saveError, setSaveError] = useState(null);
  // Re-syncs the draft to the SAVED plan whenever it changes underneath this
  // field (a successful save/clear here, or a fresh admission) — adjusted
  // DURING RENDER (React's own "adjusting state when a prop changes"
  // pattern, never a `useEffect`) so it can never race a same-tick `onChange`
  // the way an effect keyed off a passive-effect flush can (a real repro:
  // the mount-time effect for a field that appears only once its catalogue
  // loads was still pending when the doctor's first edit landed, so it fired
  // AFTER the edit and stomped the just-typed draft back to the old plan).
  const [syncedPlannedIso, setSyncedPlannedIso] = useState(plannedIso);
  if (syncedPlannedIso !== plannedIso) {
    setSyncedPlannedIso(plannedIso);
    setDraft(utcIsoToISTInputValue(plannedIso));
    setSaveError(null);
  }

  if (completed) {
    const wireValue = headerWireValue(field, value);
    return (
      <div>
        <label className={labelCls}>{label}</label>
        <div className={readonlyValueCls} data-testid={`summary-field-${key}`}>{formatDateTimeIST(wireValue) || '—'}</div>
        <div className={helperCls} data-testid={`summary-field-${key}-recorded-note`}>{DISCHARGE_DATE_RECORDED_NOTE}</div>
      </div>
    );
  }

  // ONE DOOR (orchestrator ruling) — the date is CONFIRMED but the stay is not
  // over: mirror it read-only. No input, no Save/Clear, and the key is still
  // never dispatched into the summary's own PUT (the panel excludes it
  // outright) — the server's FIX-7 `DISCHARGE_DATE_READ_ONLY` 409 now covers
  // this window too, so a stale tab gets the same refusal.
  if (confirmed) {
    return (
      <div>
        <label className={labelCls}>{DISCHARGE_DATE_CONFIRMED_LABEL}</label>
        <div className={readonlyValueCls} data-testid={`summary-field-${key}`}>{formatDateTimeIST(plannedIso) || '—'}</div>
        <div className={helperCls} data-testid={`summary-field-${key}-confirmed-note`}>{DISCHARGE_DATE_CONFIRMED_NOTE}</div>
      </div>
    );
  }

  if (!canEdit) {
    return (
      <div>
        <label className={labelCls}>{DISCHARGE_DATE_PLANNED_LABEL}</label>
        <div className={readonlyValueCls} data-testid={`summary-field-${key}`}>{formatDateTimeIST(plannedIso) || '—'}</div>
      </div>
    );
  }

  // Re-runs on every edit, whichever field changed, same as the Plan dialog
  // itself (AC-I2/AC-I3) — neither half is ever silently rewritten.
  const { date, time } = splitISTValue(draft);
  const validation = validatePlanDischarge({ date, time, admittedAt });
  const dirty = draft !== utcIsoToISTInputValue(plannedIso);
  const saveDisabled = busy || !dirty || !draft || !validation.valid;
  const clearDisabled = busy || !plannedIso;

  const runSave = async () => {
    if (saveDisabled) return;
    setBusy(true);
    setSaveError(null);
    const res = await onSavePlan(validation.plannedIso);
    setBusy(false);
    if (!res || !res.success) setSaveError(planDischargeErrorMessage(res));
  };

  const runClear = async () => {
    if (clearDisabled) return;
    setBusy(true);
    setSaveError(null);
    const res = await onSavePlan(null);
    setBusy(false);
    if (!res || !res.success) setSaveError(planDischargeErrorMessage(res));
  };

  return (
    <div>
      <label className={labelCls} htmlFor={inputId}>{DISCHARGE_DATE_PLANNED_LABEL}</label>
      <input
        id={inputId}
        type="datetime-local"
        className={inputCls}
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        data-testid={`summary-field-${key}`}
      />
      {validation.error && (
        <div className={errorCls} data-testid={`summary-field-${key}-plan-error`}>{validation.error}</div>
      )}
      {saveError && (
        <div className={errorCls} data-testid={`summary-field-${key}-error`}>{saveError}</div>
      )}
      <div className="mt-1.5 flex items-center gap-3">
        <button
          type="button"
          className="text-teal-700 dark:text-teal-300 font-medium underline text-[11px] disabled:opacity-60 disabled:no-underline"
          onClick={runSave}
          disabled={saveDisabled}
          data-testid={`summary-field-${key}-save`}
        >
          {busy ? 'Saving…' : 'Save'}
        </button>
        <button
          type="button"
          className="text-slate-600 dark:text-slate-300 font-medium underline text-[11px] disabled:opacity-60 disabled:no-underline"
          onClick={runClear}
          disabled={clearDisabled}
          data-testid={`summary-field-${key}-clear`}
        >
          Clear
        </button>
      </div>
    </div>
  );
}

/**
 * `length_of_stay` (ruling F) — a header row like any other (editable text,
 * saved as an override under its own key), but with one extra behaviour: an
 * UNTOUCHED value is not the server's static `prefill`, it is LIVE
 * -recomputed from whatever `admitted_at`/`discharged_at` currently show ON
 * THIS FORM (including an edit the doctor made this session but hasn't
 * saved yet) via the single exported `computeLengthOfStay` helper — so
 * editing the dates keeps the shown duration in sync until the doctor types
 * into this field directly. "Touched" reuses the SAME `headerOverride`
 * single-source-of-truth every other header uses (a non-null override,
 * including one loaded from a prior save, IS touched) — no separate flag.
 * Once touched, the value freezes; the "Differs from the admission record"
 * hint compares the frozen override against a FRESH recompute (not the
 * static `prefill`), and "Reset to auto" clears the override (sends `null`)
 * to resume live recompute. `computed_until_now` (still-active admission,
 * additive server flag) appends "(so far)" beside an untouched value only —
 * once the doctor supplies their own value it is no longer a running count.
 */
function LengthOfStayField({ field, admittedField, dischargedField, value, onChange }) {
  const { key, label } = field;
  const inputId = `sf-${key}`;
  const override = headerOverride(field, value);
  const touched = override !== null;

  const admittedWire = admittedField ? headerWireValue(admittedField, value) : null;
  const dischargedWire = dischargedField ? headerWireValue(dischargedField, value) : null;
  const computed = computeLengthOfStay(admittedWire, dischargedWire) || field.prefill || null;

  const displayValue = touched ? override : computed;
  const differs = touched && computed !== null && override !== computed;
  const showSoFar = !touched && !!field.computed_until_now && !!computed;

  const resetLink = touched && (
    <button
      type="button"
      className="text-teal-700 dark:text-teal-300 font-medium underline"
      onClick={() => onChange(key, null)}
      data-testid={`summary-field-${key}-reset`}
    >
      {RESET_TO_AUTO_COPY}
    </button>
  );

  if (!field.editable) {
    return (
      <div>
        <label className={labelCls}>{label}</label>
        <div className={readonlyValueCls} data-testid={`summary-field-${key}`}>
          {displayValue || '—'}
          {showSoFar && <span data-testid={`summary-field-${key}-so-far`}> (so far)</span>}
        </div>
      </div>
    );
  }

  return (
    <div>
      <label className={labelCls} htmlFor={inputId}>{label}</label>
      <input
        id={inputId}
        type="text"
        className={inputCls}
        maxLength={field.max_length || undefined}
        value={displayValue || ''}
        onChange={(e) => {
          const next = e.target.value;
          onChange(key, next === '' ? null : next);
        }}
        data-testid={`summary-field-${key}`}
      />
      {showSoFar && <div className={helperCls} data-testid={`summary-field-${key}-so-far`}>(so far)</div>}
      {(differs || resetLink) && (
        <div className={helperCls}>
          {differs && <span data-testid={`summary-field-${key}-differs`}>{DIFFERS_FROM_ADMISSION_COPY}{resetLink ? ' — ' : ''}</span>}
          {resetLink}
        </div>
      )}
    </div>
  );
}

/**
 * D2's row shape guard, client-side (`lab_lines`). A legacy STRING value for
 * `investigations` (a row saved before this delta, D7) is impossible on the
 * wire going forward — the server converts it to rows on every read — but
 * this is guarded ONCE anyway rather than trusted blind: split into lines,
 * drop blanks, one custom row per line, exactly D7's own rule. Anything that
 * isn't an array or a string (undefined/null) is `null` — the value is a row
 * array or `null`, NEVER `[]` (D2's "null = never set" semantic).
 */
export function toInvestigationRowsValue(raw) {
  if (Array.isArray(raw)) return raw;
  if (typeof raw === 'string') {
    const rows = raw
      .split('\n')
      .map((line) => line.trim())
      .filter(Boolean)
      .map((name) => ({ name, result: null, source: 'custom', procedure_id: null }));
    return rows.length > 0 ? rows : null;
  }
  return null;
}

/**
 * Client-side mirror of D4/D5's row caps (build-plan-phase1.md §R.1) for any
 * `kind:"lab_lines"` field — more rows than `INVESTIGATION_ROWS_MAX`, a name
 * over the field's own `max_length` (120), or a result over
 * `INVESTIGATION_RESULT_MAX_LENGTH` (200) is a Save-blocking HINT, the row
 * named so the doctor can find it; the server (BE-C1) remains the enforcer.
 * Exported for `DischargeSummaryPanel.jsx`'s `deriveSaveBlockedReason`, same
 * relationship `getHeaderDateRangeError` already has with its own 422 mirror.
 */
export function getInvestigationRowsError(fields = [], value = {}) {
  const labFields = fields.filter((field) => field && field.kind === LAB_LINES_KIND);
  for (const field of labFields) {
    const rows = toInvestigationRowsValue(value[field.key]);
    if (!Array.isArray(rows)) continue;
    if (rows.length > INVESTIGATION_ROWS_MAX) {
      return `${field.label}: no more than ${INVESTIGATION_ROWS_MAX} investigations are allowed.`;
    }
    const nameLimit = field.max_length || INVESTIGATION_NAME_MAX_LENGTH_DEFAULT;
    for (let i = 0; i < rows.length; i += 1) {
      const row = rows[i] || {};
      if ((row.name || '').length > nameLimit) {
        return `${field.label}, row ${i + 1}: the test name is over ${nameLimit} characters.`;
      }
      if ((row.result || '').length > INVESTIGATION_RESULT_MAX_LENGTH) {
        return `${field.label}, row ${i + 1}: the result is over ${INVESTIGATION_RESULT_MAX_LENGTH} characters.`;
      }
    }
  }
  return null;
}

// DISCHARGE-TEMPLATES 1b Slice 1 (FE-6/§2.9) — a plain calendar-day regex,
// mirroring the server's `_validate_and_normalize` date branch for DISPLAY
// purposes only (deciding whether to show the legacy amber note / the
// cross-field warning). Never converts a timezone, never wraps `new Date()`
// on a bare day — string-only, matching standing DoD #10's ban.
const ISO_DATE_RE = /^\d{4}-\d{2}-\d{2}$/;

// Ratified copy (build-plan-1b-slice1.md §3 FE-6 (b)), verbatim.
export function followUpLegacyNoteCopy(legacyText) {
  return `Previously saved: ${legacyText} — it will keep printing until you choose a date.`;
}

// Ratified copy (copy-ratified-slice1.md §A #7).
export const FOLLOW_UP_BEFORE_DOD_WARNING_COPY = 'The follow-up date is before the date of discharge. Check it before printing.';

export const SAVE_AS_TEMPLATE_TRIGGER_COPY = 'Save as template';
// Ratified copy (copy-ratified-slice1.md §A #8a). The cap reason (#8b) is
// the SAME sentence `snippetLimitReachedCopy` builds for the server-driven
// 409 (`dischargeSnippetApi.js`) — single source, never re-typed, so the
// pre-emptive disabled reason and the server's own refusal always agree.
export const SAVE_AS_TEMPLATE_EMPTY_REASON = 'Type the text first, then save it as a template.';

/**
 * Owner-locked print line is `Follow-up on 20 Sep 2026` (§2.9) — this helper
 * is the FORM's own non-blocking cross-field mirror, R-F5's sibling: a
 * follow-up date earlier than the effective Date of discharge is a WARNING,
 * never a Save/Print block (same posture as `isDischargeDateEmpty`).
 * `discharged_at` is a UTC-ISO datetime — its IST CALENDAR DAY is read via
 * the SAME `utcIsoToISTInputValue` the header's own datetime input already
 * uses (that is `discharged_at`'s established path, not the follow-up date
 * path standing DoD #10 bans conversions on); the two calendar-day STRINGS
 * are then compared lexicographically (`YYYY-MM-DD` sorts the same as it
 * dates) — no `Date` object, no timezone math, on the follow-up side.
 * Returns `null` when either date is empty/unparseable/a still-legacy value
 * (nothing valid to compare) — an empty DOD warns about nothing (mirrors
 * `isDischargeDateEmpty`'s own null-safety).
 */
export function getFollowUpBeforeDischargeWarning(fields = [], value = {}) {
  const dischargedField = fields.find((f) => f && f.key === 'discharged_at');
  // Dispatch by FLAG, never by key name (§2.10) — the only narrative,
  // non-header field carrying `value_type:"date"` this slice is `follow_up`,
  // but nothing here hardcodes that key.
  const followUpField = fields.find((f) => f && f.value_type === 'date' && f.kind !== HEADER_AUTOFILL_KIND);
  if (!dischargedField || !followUpField) return null;
  const dischargedWire = headerWireValue(dischargedField, value);
  if (!dischargedWire) return null;
  const dischargedLocal = utcIsoToISTInputValue(dischargedWire);
  const dischargedDay = dischargedLocal ? dischargedLocal.slice(0, 10) : null;
  if (!dischargedDay) return null;
  const followUpValue = value[followUpField.key];
  if (!followUpValue || !ISO_DATE_RE.test(followUpValue)) return null;
  return followUpValue < dischargedDay ? FOLLOW_UP_BEFORE_DOD_WARNING_COPY : null;
}

/**
 * Client-side mirror of the 422 `INVALID_DATE_RANGE` (§2.7) — exported so a
 * hosting panel can disable Save with the same reason this component already
 * shows inline (the panel owns the Save button, not this file). Returns the
 * WHY string, or `null` when either date is missing/unparseable/in range.
 */
export function getHeaderDateRangeError(fields = [], value = {}) {
  const admittedField = fields.find((f) => f && f.key === 'admitted_at');
  const dischargedField = fields.find((f) => f && f.key === 'discharged_at');
  if (!admittedField || !dischargedField) return null;
  const admitted = headerWireValue(admittedField, value);
  const discharged = headerWireValue(dischargedField, value);
  if (!admitted || !discharged) return null;
  const admittedMs = Date.parse(admitted);
  const dischargedMs = Date.parse(discharged);
  if (Number.isNaN(admittedMs) || Number.isNaN(dischargedMs)) return null;
  return dischargedMs < admittedMs ? DATE_RANGE_ERROR_COPY : null;
}

/**
 * Owner ruling I addendum (discharge-templates-phase1-2026-09-13.md,
 * DOD-WARN-FE) — single source for "is the effective Date of discharge
 * (`value ?? prefill`, same resolver every header uses) empty right now".
 * Consumed by BOTH the inline warning under the header input
 * (`HeaderField`, fed with just that one field) AND
 * `DischargeSummaryPanel.jsx`'s sticky-footer line (fed the full `fields[]`)
 * — one computation, so the two surfaces can never disagree about "empty".
 * Never feeds `deriveSaveBlockedReason`; this is a WARNING, never a Save/
 * Print block. Returns `false` when the catalogue carries no `discharged_at`
 * key at all (old backend / hidden-by-org, §2.7 forward-compat — nothing to
 * warn about).
 */
export function isDischargeDateEmpty(fields = [], value = {}) {
  const dischargedField = fields.find((f) => f && f.key === 'discharged_at');
  if (!dischargedField) return false;
  return !headerWireValue(dischargedField, value);
}

/**
 * Pure validity check for the "Summary" step (copy-deck §5). Cause of death
 * is written ONLY by `DischargeDialog`'s `/discharge/complete` call (C5
 * FE-4 reconcile — see SummaryFields JSDoc); this check reads the CAPTURED
 * value back off the `admission` (single writer), never a locally-edited
 * `value.cause_of_death` (SummaryFields never edits it). No template-aware
 * required validation in Phase 1 (DISCHARGE-TEMPLATES build-plan FE-2 DoD).
 */
export function isSummaryValid(dischargeType, admission = {}) {
  if (dischargeType !== DISCHARGE_TYPE.DECEASED) return true;
  const cod = (admission && admission.cause_of_death) || {};
  return !!(
    cod.immediate_cause && String(cod.immediate_cause).trim()
    && cod.death_at && String(cod.death_at).trim()
  );
}

/**
 * One narrative row, driven entirely by the field object the API returned —
 * no key is ever hardcoded here (RISK-2: a new canonical key needs no FE
 * release). `editable:false` renders read-only, matching a header row's
 * treatment even though Phase 1's catalogue only marks headers non-editable
 * today (§2.1) — this keeps the branch alive for a future non-editable
 * narrative key without another release.
 *
 * DISCHARGE-TEMPLATES 1b Slice 1 (FE-6) additions, both dispatched by FLAG,
 * never by key name:
 *  - `value_type === "date"` → a plain `<input type="date">` bound to the
 *    RAW `YYYY-MM-DD` wire string, no UTC conversion (standing DoD #10).
 *    `field.legacy_text_value` (present only when the stored value pre-dates
 *    this slice and does not parse as a date, §2.9) shows the old text
 *    read-only in an amber note and renders the input EMPTY — picking a
 *    real date simply overwrites the local value, no separate "convert"
 *    action needed. `followUpWarning`, computed ONCE by `SummaryFields` via
 *    `getFollowUpBeforeDischargeWarning`, is a WARNING only (never blocks
 *    Save/Print, same posture as `isDischargeDateEmpty`).
 *  - `field.snippets === true` → mounts `SnippetPicker` + a "Save as
 *    template" trigger beside the label, gated on `canManageTemplates`
 *    (locked/permission, derived ONCE by `SummaryFields`) AND `editable`
 *    (this function's own existing gate) — ABSENT, never disabled, on
 *    either. "Save as template" is separately disabled-with-reason when the
 *    field is empty/whitespace or the field's own snippet cap is reached.
 *
 * DISCHARGE-TEMPLATES 1b Slice 1 (COMP-FE) addition, checked BEFORE the
 * `!editable` branch (so a locked/read-only item field still renders chips,
 * never the generic raw-text read-only row) and dispatched by the SAME
 * kind of flag, never a key literal:
 *  - `field.snippet_mode === "item"` → mounts `ComplaintItemsField` instead
 *    of a textarea (today only `chief_complaints`, §1.1). `field.snippets`
 *    is a DERIVED MIRROR of `snippet_mode` (backend-computed, §1.1) so it
 *    stays `true` here too — this component never reads it for the item
 *    branch, only `snippet_mode` decides. An UNKNOWN `snippet_mode` (a
 *    future mode this FE has never heard of) with `snippets: true` falls
 *    through to the paragraph `SnippetPicker` branch below unchanged —
 *    never a crash. No "Save as template"/"Use template" toolbar on an item
 *    field — new items are learned server-side at save (§1.4), there is no
 *    client-triggered save action to offer.
 */
function NarrativeField({
  field, value, onChange, error, canManageTemplates, snippetEntry, onOpenSnippets, onDeleteSnippet,
  onRequestSaveTemplate, followUpWarning,
}) {
  const { key, label, editable, max_length: maxLength, kind } = field;
  const inputId = `sf-${key}`;

  if (kind === LAB_LINES_KIND) {
    // D1/D2 (§R.1) — the ONLY key on this kind today is `investigations`,
    // but nothing here is hardcoded to that key. `editable` still gates
    // read-only vs editable the SAME way every other field does (the
    // catalogue keeps `editable:true` for it, so this stays a live branch
    // for a future non-editable `lab_lines` key without another release).
    // Follow-on DEF-INV-FE — `field.prefill` (DEF-INV-BE: the organisation's
    // default row list) is passed straight through; `InvestigationRowsField`
    // decides ONCE at mount whether to seed from it (its own JSDoc).
    const rowsValue = toInvestigationRowsValue(editable ? value : field.value);
    return (
      <div data-testid={`summary-field-${key}`}>
        {/* UX v2 spec §5 — solid grouping card around the leaf widget; the
            widget's own internal spacing (space-y-2/space-y-1) is unchanged. */}
        <div className="rounded-lg border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-900 p-3">
          <InvestigationRowsField
            value={rowsValue}
            prefill={field.prefill}
            onChange={(rows) => onChange({ target: { value: rows } })}
            disabled={!editable}
            label={label}
            maxLength={maxLength || INVESTIGATION_NAME_MAX_LENGTH_DEFAULT}
            error={error && error.message}
            errorRow={error && error.row != null ? error.row : null}
          />
        </div>
      </div>
    );
  }

  if (key === BULLETED_NARRATIVE_KEY) {
    // Bulleted/plain line editor (ruling B + FIX-2 + FIX-3) — one row per
    // line, each line independently toggled bullet or plain (owner's
    // live-print bug: every line, incl. the "Rx" heading and "X5 Days"
    // trailer, printed as a bullet — the doctor now chooses per line).
    // The bullet/plain marker lives inside the stored value itself
    // (leading "- " = bullet, frozen convention `BulletLinesField.jsx`
    // documents), never rendered outside the text as a separate flag. Same
    // `editable` gate every other kind uses: read-only renders via the
    // component's own `disabled` view (bulleted/plain, non-interactive)
    // instead of the generic read-only row below, matching `lab_lines`'
    // treatment. No `maxLength` here — Phase 1's catalogue max_length was
    // sized for the old single-textarea box; a per-LINE cap of the same
    // number would be far too strict for a medicine name (`SummaryFields.jsx`'s
    // other narrative textarea keeps its own field-level maxLength unchanged).
    //
    // DISCHARGE-TEMPLATES 1b Slice 1 (MED-FE) — the SAME `field.snippets`
    // paragraph toolbar (`SnippetPicker` + "Save as template") the plain
    // -textarea branch below already has, dispatched by the identical flag,
    // never a key literal. `BulletLinesField` itself is NOT modified: a
    // template Append/Replace goes through the SAME `onChange(next)` path
    // any other edit does, so it lands on this field's `value` prop like
    // any external reset — `BulletLinesField`'s existing re-seed-on
    // -external-change effect (DEF-1) already rebuilds `rows` from the new
    // stored string, one row per line, bullet/plain markers preserved by
    // construction (`parseStoredLines` reads each line's own `"- "` prefix,
    // it never re-derives a line's mode from anything else). The stored
    // `value` this field carries is ALREADY `toExternalValue`-filtered of
    // any blank trailing row (every `commit()` in `BulletLinesField` emits
    // through that filter), so Append never needs a separate "skip the
    // trailing empty row" step here. `templatesEnabled` folds in `editable`
    // explicitly (unlike the plain-textarea branch, which is only reached
    // once `!editable` has already returned) — this branch is entered
    // BEFORE that check.
    const templatesEnabled = !!field.snippets && canManageTemplates && editable;
    const bulletedRowsValue = editable ? value : field.value;
    const bulletedEmptyValue = !bulletedRowsValue || !String(bulletedRowsValue).trim();
    const bulletedAtCap = !!snippetEntry
      && typeof snippetEntry.count === 'number' && typeof snippetEntry.limit === 'number'
      && snippetEntry.count >= snippetEntry.limit;
    const bulletedSaveDisabledReason = bulletedEmptyValue
      ? SAVE_AS_TEMPLATE_EMPTY_REASON
      : (bulletedAtCap ? snippetLimitReachedCopy(snippetEntry?.limit) : null);
    return (
      <div data-testid={`summary-field-${key}`}>
        {templatesEnabled && (
          <div className="flex items-center justify-end gap-1.5 mb-1.5" data-testid={`summary-field-${key}-template-toolbar`}>
            <SnippetPicker
              fieldKey={key}
              fieldLabel={label}
              value={bulletedRowsValue}
              onChange={(next) => onChange({ target: { value: next } })}
              ownerDoctorName={snippetEntry?.ownerDoctorName ?? null}
              snippets={snippetEntry?.snippets || []}
              loading={snippetEntry?.status === 'loading'}
              error={snippetEntry?.status === 'error' ? snippetEntry.error : null}
              onDelete={(snippetId) => onDeleteSnippet(key, snippetId)}
              onOpen={() => onOpenSnippets && onOpenSnippets(key)}
            />
            <button
              type="button"
              disabled={!!bulletedSaveDisabledReason}
              title={bulletedSaveDisabledReason || undefined}
              onClick={() => onRequestSaveTemplate(key)}
              data-testid={`summary-field-${key}-save-template`}
              className="px-2.5 py-1 rounded-md border border-teal-400 dark:border-teal-500 bg-teal-50 dark:bg-teal-900/40 text-teal-800 dark:text-teal-200 text-[11.5px] font-semibold disabled:opacity-60"
            >
              {SAVE_AS_TEMPLATE_TRIGGER_COPY}
            </button>
          </div>
        )}
        {/* UX v2 spec §5 — solid grouping card around the leaf widget; the
            widget's own internal spacing (space-y-1) is unchanged. */}
        <div className="rounded-lg border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-900 p-3">
          <BulletLinesField
            value={bulletedRowsValue}
            onChange={(next) => onChange({ target: { value: next } })}
            disabled={!editable}
            label={label}
            error={error && error.message}
          />
        </div>
      </div>
    );
  }

  // DISCHARGE-TEMPLATES 1b Slice 1 (COMP-FE) — dispatched by FLAG
  // (`snippet_mode`), never by key name. Checked BEFORE the generic
  // `!editable` branch below so a locked/read-only item field still renders
  // its chips (own JSDoc above), not the generic raw-text read-only row.
  // `canManageTemplates` (locked/permission, derived ONCE by
  // `SummaryFields`) combined with this function's own `editable` gate —
  // the SAME two signals the paragraph toolbar above already reads — decide
  // whether the picker is interactive; the chips themselves always render.
  if (field.snippet_mode === 'item') {
    const itemEditable = editable && canManageTemplates;
    return (
      <div data-testid={`summary-field-${key}`}>
        <label className={narrativeLabelCls} htmlFor={inputId}>
          {label}
          {field.required && <span className={requiredSuffixCls}>(required)</span>}
        </label>
        <ComplaintItemsField
          fieldKey={key}
          value={itemEditable ? value : field.value}
          onChange={(next) => onChange({ target: { value: next } })}
          readOnly={!itemEditable}
          ownerDoctorName={snippetEntry?.ownerDoctorName ?? null}
          suggestions={snippetEntry?.snippets || []}
          loading={snippetEntry?.status === 'loading'}
          error={snippetEntry?.status === 'error' ? snippetEntry.error : null}
          onOpenSuggestions={() => onOpenSnippets && onOpenSnippets(key)}
          onDeleteSuggestion={(snippetId) => onDeleteSnippet(key, snippetId)}
        />
        {error && error.message && (
          <div className={errorCls} data-testid={`summary-field-${key}-error`}>{error.message}</div>
        )}
      </div>
    );
  }

  if (!editable) {
    // Read-only fields are never part of the editable `value` map — display
    // the API's own `field.value` directly (§2.2), no parent seeding needed.
    return (
      <div>
        <label className={narrativeLabelCls}>{label}</label>
        <div className={readonlyValueCls} data-testid={`summary-field-${key}`}>{field.value || '—'}</div>
      </div>
    );
  }

  if (field.value_type === 'date') {
    // §2.9 — a legacy free-text value never parses as `YYYY-MM-DD`; the
    // input renders EMPTY in that case (nothing valid to show) and the raw
    // text is surfaced read-only below it, verbatim, never rewritten.
    const isLegacyText = !!field.legacy_text_value && !ISO_DATE_RE.test(value || '');
    return (
      <div>
        <label className={narrativeLabelCls} htmlFor={inputId}>{label}</label>
        {isLegacyText && (
          <div className={dodWarningCls} role="status" data-testid={`summary-field-${key}-legacy-note`}>
            <DodEmptyWarningIcon />
            <span>{followUpLegacyNoteCopy(value)}</span>
          </div>
        )}
        <input
          id={inputId}
          type="date"
          className={`${inputCls} mt-1.5`}
          value={isLegacyText ? '' : (value || '')}
          onChange={(e) => {
            const next = e.target.value;
            onChange({ target: { value: next === '' ? null : next } });
          }}
          data-testid={`summary-field-${key}`}
        />
        {followUpWarning && (
          <div className={dodWarningCls} role="status" data-testid={`summary-field-${key}-dod-warning`}>
            <DodEmptyWarningIcon />
            <span>{followUpWarning}</span>
          </div>
        )}
        {error && error.message && (
          <div className={errorCls} data-testid={`summary-field-${key}-error`}>{error.message}</div>
        )}
      </div>
    );
  }

  const size = NARRATIVE_SIZE[key] || NARRATIVE_SIZE_DEFAULT;
  // Dispatch by the ONE additive flag (§2.10) — never by key name. Absent
  // (forward-compat, an older backend) or `false` ⇒ plain textarea,
  // unchanged from Phase 1. `canManageTemplates` (locked/permission, derived
  // once by `SummaryFields`) gates BOTH controls off entirely — never merely
  // disabled — same as the `!editable` branch above.
  const templatesEnabled = !!field.snippets && canManageTemplates;
  const emptyValue = !value || !String(value).trim();
  const atCap = !!snippetEntry
    && typeof snippetEntry.count === 'number' && typeof snippetEntry.limit === 'number'
    && snippetEntry.count >= snippetEntry.limit;
  const saveDisabledReason = emptyValue ? SAVE_AS_TEMPLATE_EMPTY_REASON : (atCap ? snippetLimitReachedCopy(snippetEntry?.limit) : null);

  return (
    <div>
      <div className="flex items-center justify-between gap-2 flex-wrap mb-1.5">
        <label className={narrativeLabelInlineCls} htmlFor={inputId}>
          {label}
          {field.required && <span className={requiredSuffixCls}>(required)</span>}
        </label>
        {templatesEnabled && (
          <div className="flex items-center gap-1.5" data-testid={`summary-field-${key}-template-toolbar`}>
            <SnippetPicker
              fieldKey={key}
              fieldLabel={label}
              value={value}
              onChange={(next) => onChange({ target: { value: next } })}
              ownerDoctorName={snippetEntry?.ownerDoctorName ?? null}
              snippets={snippetEntry?.snippets || []}
              loading={snippetEntry?.status === 'loading'}
              error={snippetEntry?.status === 'error' ? snippetEntry.error : null}
              onDelete={(snippetId) => onDeleteSnippet(key, snippetId)}
              onOpen={() => onOpenSnippets && onOpenSnippets(key)}
            />
            <button
              type="button"
              disabled={!!saveDisabledReason}
              title={saveDisabledReason || undefined}
              onClick={() => onRequestSaveTemplate(key)}
              data-testid={`summary-field-${key}-save-template`}
              className="px-2.5 py-1 rounded-md border border-teal-400 dark:border-teal-500 bg-teal-50 dark:bg-teal-900/40 text-teal-800 dark:text-teal-200 text-[11.5px] font-semibold disabled:opacity-60"
            >
              {SAVE_AS_TEMPLATE_TRIGGER_COPY}
            </button>
          </div>
        )}
      </div>
      <textarea
        id={inputId}
        className={`${narrativeTextareaCls} ${size.cls}`}
        rows={size.rows}
        maxLength={maxLength || undefined}
        value={value || ''}
        onChange={onChange}
        data-testid={`summary-field-${key}`}
      />
      {key === HOSPITAL_COURSE_KEY && (
        <div className={helperCls} data-testid={`summary-field-${key}-helper`}>{HOSPITAL_COURSE_HELPER_COPY}</div>
      )}
      {error && error.message && (
        <div className={errorCls} data-testid={`summary-field-${key}-error`}>{error.message}</div>
      )}
    </div>
  );
}

/**
 * SummaryFields (DISCHARGE-TEMPLATES Phase 1 · FE-2 + FE-3) — renders the
 * discharge summary **entirely from the API's `fields[]` array** (frozen
 * contract §2.2 `GET .../discharge-summary`). No canonical key is declared
 * client-side: `fields` is pre-sorted and pre-filtered by the server (hidden
 * keys are simply absent — the FE adds no filtering), so a new/renamed/
 * relabelled catalogue key renders correctly with zero FE changes.
 *
 * Kind handling (§2.1/§2.7): `kind !== "header_autofill"` — `narrative` and
 * any kind the FE has never heard of — renders as `NarrativeField` (a
 * labelled textarea, or a read-only row when `editable:false`).
 * `kind === "lab_lines"` (R.1/R.2 round-1 rework, FE-C2 — today only
 * `investigations`) is the ONE exception inside that same branch: it renders
 * `InvestigationRowsField` (FE-C1) instead of a textarea, `disabled` mirrored
 * off the field's own `editable` exactly like every other kind's read-only
 * treatment. Every other kind's rendering is UNCHANGED by this branch.
 * `kind === "header_autofill"` (`patient_name`, `ip_number`, `admitted_at`,
 * `discharged_at`, `length_of_stay`, `mlc_no`, `doctor_signature`) renders as
 * `HeaderField` — EXCEPT `length_of_stay` (ruling F), which renders via
 * `LengthOfStayField` instead (own JSDoc above): live-recomputed from the
 * form's current `admitted_at`/`discharged_at` while untouched, frozen once
 * the doctor types into it, "Reset to auto" to resume, "(so far)" when the
 * server flags `computed_until_now` (admission still active):
 * `value_type:"datetime"` ⇒ `<input type="datetime-local">` bound through
 * `formatIST`'s `utcIsoToISTInputValue`/`istInputValueToUtcIso` (IST wall
 * clock in the input, UTC ISO ms on the wire — `new Date(naiveString)` is
 * BANNED, bk-646); `value_type:"text"` (or forward-compat unknown) ⇒ a text
 * input. Every header shows `value ?? prefill` and, when a header carries an
 * override that differs from `prefill`, the ratified
 * "Differs from the admission record" line beside it. MLC (`mlc_no`) is
 * always rendered and editable regardless of any `is_mlc` flag (§1b) — this
 * component gates nothing of its own on it. `getHeaderDateRangeError` is
 * the exported, single-source mirror of the server's 422
 * `INVALID_DATE_RANGE`; shown inline here and exported for the hosting
 * panel (`DischargeSummaryPanel.jsx`, not owned by this file) to also gate
 * Save on.
 *
 * Cause-of-death (ONE writer — INV-7, unchanged): `DischargeDialog` is the
 * single writer of cause-of-death, sent on `/discharge/complete`.
 * SummaryFields renders it READ-ONLY from `admission.cause_of_death` — it
 * is NEVER part of `value`/`onChange` here.
 *
 * @param admission     admission row — `cause_of_death`, `referral_note`,
 *                        `referral_reason` (header values now come from
 *                        `fields`, not `admission`)
 * @param dischargeType DISCHARGE_TYPE key or null — gates the death/referral blocks
 * @param fields        `fields[]` from the GET/PUT envelope (§2.2), already
 *                        ordered and filtered by the server — SummaryFields
 *                        sorts/hides nothing
 * @param value         { [canonicalKey]: string|null } — every editable
 *                        field's (narrative AND header) current edited
 *                        value, keyed by `field.key`, saved together in the
 *                        one `{values}` PUT map (§2.3)
 * @param onChange      (key, nextValue) => void — parent owns `value`
 * @param errors        verdict-r2.md ruling C-3 — `{ [canonicalKey]: { message, row? } }`,
 *                        the LAST save's field-named 422/413, built by the hosting panel
 *                        (`DischargeSummaryPanel.jsx`) off the response's `key`/`row` extras.
 *                        Rendered inline under that field only; a key with no entry renders
 *                        nothing extra. `row` (0-based) is investigation-rows-only.
 * @param locked        owner ruling I addendum (DOD-WARN-FE) — every gate on this
 *                        component besides `discharged_at` (own JSDoc, `DischargeDateField`
 *                        reads `admission.status` directly, not this flag).
 * @param canEditDischargeDate FIX-7-FE — ADMIT_PATIENT OR DISCHARGE_PATIENT (the SAME
 *                        OR-gate `PlanDischargeDialog`'s own trigger uses), gating ONLY the
 *                        `discharged_at` field's edit controls pre-completion. Defaults `true`
 *                        so every pre-existing caller/test is unaffected.
 * @param onSaveDischargeDate FIX-7-FE — `(iso|null) => Promise<{success, data?, error?, code?}>`,
 *                        the discharge-PLAN setter (`patchAdmission`, DI — this component never
 *                        imports the API itself, same convention `PlanDischargeDialog.onSave`
 *                        already sets). Defaults to a no-op so a caller that never wires it
 *                        (a locked/read-only render) can't crash on a stray click.
 * @param canEditSummary DISCHARGE-TEMPLATES 1b Slice 1 (FE-6/FE-7) — the panel's own
 *                        `DISCHARGE_PATIENT` permission signal (`usePermission`, single
 *                        source, R3/bk-1202). Combined with `locked` (ONCE, here) into
 *                        `canManageTemplates` — the gate for BOTH template controls, ABSENT
 *                        (never merely disabled) on either. Defaults `true` so every
 *                        pre-existing caller/test that never heard of templates is
 *                        unaffected.
 * @param snippetDataByField DISCHARGE-TEMPLATES 1b Slice 1 (FE-7) — `{ [fieldKey]:
 *                        { status, snippets, ownerDoctorName, count, limit, error } }`,
 *                        FE-1's `useDischargeSnippetStore.byKey` re-keyed by canonical field
 *                        key (the store's own key also folds in `admissionId` — the hosting
 *                        panel does that re-keying, this component never sees an admission
 *                        id). An absent entry (key never loaded) renders the picker's own
 *                        loading/empty state, never a crash.
 * @param onOpenSnippets  DISCHARGE-TEMPLATES 1b Slice 1 (FE-7) — `(fieldKey) => void`,
 *                        forwarded to `SnippetPicker`'s `onOpen` (fires the store's `load`).
 * @param onCreateSnippet DISCHARGE-TEMPLATES 1b Slice 1 (FE-7) — `(fieldKey, {title, text})
 *                        => Promise<{success, ...}>`, wired to `SaveAsTemplateModal`'s
 *                        `onSave`.
 * @param onDeleteSnippet DISCHARGE-TEMPLATES 1b Slice 1 (FE-7) — `(fieldKey, snippetId) =>
 *                        Promise`, forwarded to `SnippetPicker`'s `onDelete`.
 */
export default function SummaryFields({
  admission, dischargeType, fields = [], value = {}, onChange, errors = {}, locked = false,
  canEditSummary = true, snippetDataByField = {}, onOpenSnippets, onCreateSnippet, onDeleteSnippet,
  canEditDischargeDate = true, onSaveDischargeDate = () => Promise.resolve({ success: false }),
}) {
  const setField = (key) => (e) => onChange(key, e.target.value);
  // Read-only — captured by DischargeDialog's /discharge/complete, never edited here.
  const cod = (admission && admission.cause_of_death) || EMPTY_CAUSE_OF_DEATH;

  const fmt = (iso) => (iso ? new Date(iso).toLocaleString() : '—');

  // FIX-7-FE — "completed" for the discharge-date field is the admission's
  // OWN terminal-ness (DECEASED included — a death time is a recorded fact,
  // never a free setter), deliberately NOT the same `locked` this component
  // otherwise honours (`locked` carves DECEASED OUT, BE-7's narrative-fields
  // carve-out, which does not apply to this one field).
  const dischargeDateCompleted = isTerminal(admission && admission.status);
  // DOD rule v3 — "the date is a CONFIRMED decision" comes from the ONE
  // shared predicate, never a status comparison here; `completed` still wins
  // (a stamped discharge shows the recorded value + its correction note).
  const dischargeDateConfirmed = !dischargeDateCompleted && dodIsConfirmed(admission);

  // DISCHARGE-TEMPLATES 1b Slice 1 — ONE derived gate for both template
  // controls (never re-derived per field/branch): a locked/terminal summary
  // or a caller who lacks `DISCHARGE_PATIENT` gets NO picker, NO save
  // trigger, on either template-enabled field — same "absent, not disabled"
  // posture the read-only branch above already has.
  const canManageTemplates = canEditSummary && !locked;
  // R-F5 (owner) — DECEASED hides the follow-up row entirely, dispatched by
  // FLAG (`value_type === "date"`, this slice's only such narrative field),
  // never by an inline 'follow_up' key literal. `instructions` (plain text)
  // is untouched and still renders on a death sheet.
  const narrativeFields = fields.filter((field) => field && field.kind !== HEADER_AUTOFILL_KIND)
    .filter((field) => !(dischargeType === DISCHARGE_TYPE.DECEASED && field.value_type === 'date'));
  const headerFields = fields.filter((field) => field && field.kind === HEADER_AUTOFILL_KIND);
  const doctorField = headerFields.find((field) => field.key === 'doctor_signature');
  // Ruling F — `length_of_stay` is pulled out of the generic map (same
  // treatment `doctor_signature` gets) so it can be re-inserted right after
  // `discharged_at` regardless of the catalogue's own field order, and so it
  // can get its own live-recompute component instead of the generic
  // `HeaderField`. Absent from `fields[]` (old backend, §2.7 forward-compat)
  // -> `losField` is `undefined` -> nothing renders, per spec.
  const losField = headerFields.find((field) => field.key === LENGTH_OF_STAY_KEY);
  const admittedField = headerFields.find((field) => field.key === 'admitted_at');
  const dischargedField = headerFields.find((field) => field.key === 'discharged_at');
  const gridHeaderFields = headerFields.filter(
    (field) => field.key !== 'doctor_signature' && field.key !== LENGTH_OF_STAY_KEY,
  );
  const dateRangeError = getHeaderDateRangeError(fields, value);
  // DISCHARGE-TEMPLATES 1b Slice 1 — non-blocking cross-field mirror,
  // recomputed live off the CURRENT `value` map every render, so moving the
  // Date of discharge re-evaluates it instantly without touching the
  // follow-up field's own stored value (own JSDoc above).
  const followUpWarning = getFollowUpBeforeDischargeWarning(fields, value);

  // DISCHARGE-TEMPLATES 1b Slice 1 (FE-6) — which field's "Save as template"
  // modal is open, by canonical key or `null`. ONE shared modal instance
  // (below), not one per field — same pattern `DischargeSummaryPanel.jsx`'s
  // own single `ConfirmationModal` instance uses.
  const [saveModalField, setSaveModalField] = useState(null);
  const saveModalFieldObj = narrativeFields.find((f) => f && f.key === saveModalField) || null;
  const saveModalEntry = saveModalField ? snippetDataByField[saveModalField] : null;

  return (
    <div className="space-y-4" data-testid="summary-fields">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-testid="summary-header-block">
        {gridHeaderFields.map((field) => (
          <React.Fragment key={field.key}>
            {field.key === 'discharged_at' ? (
              <DischargeDateField
                field={field}
                value={value}
                admission={admission}
                completed={dischargeDateCompleted}
                confirmed={dischargeDateConfirmed}
                canEdit={canEditDischargeDate}
                onSavePlan={onSaveDischargeDate}
              />
            ) : (
              <HeaderField field={field} value={value} onChange={onChange} error={errors[field.key]} dateLocked={dischargeDateConfirmed || dischargeDateCompleted} />
            )}
            {field.key === 'discharged_at' && losField && (
              <LengthOfStayField
                field={losField}
                admittedField={admittedField}
                dischargedField={dischargedField}
                value={value}
                onChange={onChange}
              />
            )}
          </React.Fragment>
        ))}
      </div>
      {dateRangeError && (
        <div
          className="rounded-lg border border-red-300 dark:border-red-700 bg-red-50 dark:bg-red-900/20 text-red-800 dark:text-red-200 text-[12.5px] p-2.5"
          data-testid="summary-header-date-range-error"
        >
          {dateRangeError}
        </div>
      )}

      {/* UX v2 spec §4/§1 — the narrative loop gets its own 20px rhythm
          (space-y-5) instead of the outer 16px (space-y-4); the header
          grid/banners/death/referral/doctor blocks above and below keep the
          outer rhythm. Group captions are emitted inline, once per group,
          the first time a key from that group is encountered, walking the
          server's own order — never resorts, never hides a field. */}
      <div className="space-y-5">
        {(() => {
          const printedGroups = new Set();
          return narrativeFields.map((field) => {
            const group = NARRATIVE_GROUPS.find((g) => g.fieldKeys.includes(field.key));
            const caption = (group && !printedGroups.has(group.key)) ? narrativeGroupCaption(group) : null;
            if (group) printedGroups.add(group.key);
            return (
              <React.Fragment key={field.key}>
                {caption}
                <NarrativeField
                  field={field}
                  value={value[field.key]}
                  onChange={setField(field.key)}
                  error={errors[field.key]}
                  canManageTemplates={canManageTemplates}
                  snippetEntry={snippetDataByField[field.key]}
                  onOpenSnippets={onOpenSnippets}
                  onDeleteSnippet={onDeleteSnippet}
                  onRequestSaveTemplate={setSaveModalField}
                  followUpWarning={field.value_type === 'date' ? followUpWarning : null}
                />
              </React.Fragment>
            );
          });
        })()}
      </div>

      {dischargeType === DISCHARGE_TYPE.DECEASED && (
        <div className="rounded-lg border border-red-300 dark:border-red-700 p-3 space-y-1 text-[13px] text-slate-800 dark:text-slate-100" data-testid="summary-death-block">
          <div className="text-[12.5px] font-semibold text-slate-800 dark:text-slate-100">Cause of death</div>
          <div data-testid="summary-cod-immediate">Immediate cause: {cod.immediate_cause || '—'}</div>
          <div data-testid="summary-cod-antecedent">Underlying / antecedent cause: {cod.antecedent_cause || '—'}</div>
          <div data-testid="summary-cod-death-at">Date &amp; time of death: {fmt(cod.death_at)}</div>
        </div>
      )}

      {dischargeType === DISCHARGE_TYPE.REFERRED && (
        <div className="rounded-lg border border-slate-200 dark:border-slate-700 p-3 space-y-1 text-[13px] text-slate-800 dark:text-slate-100" data-testid="summary-referral-block">
          <div>Referred to: {(admission && admission.referral_note) || '—'}</div>
          <div>Reason for referral: {(admission && admission.referral_reason) || '—'}</div>
        </div>
      )}

      {doctorField && (
        <div className="text-[13px] text-slate-800 dark:text-slate-100" data-testid="summary-doctor-block">
          <HeaderField field={doctorField} value={value} onChange={onChange} />
          <div className={helperCls}>Signature &amp; seal of treating consultant</div>
        </div>
      )}

      {/* DISCHARGE-TEMPLATES 1b Slice 1 (FE-6/FE-7) — ONE shared modal
          instance for every template-enabled field; `onSave` bubbles to the
          hosting panel's `createSnippet` and only closes the modal on a
          SUCCESSFUL save (the modal's own JSDoc — a thrown/rejected `onSave`
          keeps it open with its own inline error, never silently loses the
          doctor's typed title). Saving a template never touches `value` or
          calls `onChange` — the field itself is untouched (§2.5). */}
      <SaveAsTemplateModal
        isOpen={!!saveModalField}
        field={saveModalField}
        fieldLabel={saveModalFieldObj ? saveModalFieldObj.label : undefined}
        text={saveModalField ? (value[saveModalField] || '') : ''}
        existingTitles={(saveModalEntry?.snippets || []).map((s) => s.title)}
        onSave={async (payload) => {
          const res = await onCreateSnippet(payload.field, { title: payload.title, text: payload.text });
          if (!res || !res.success) {
            throw new Error((res && res.error) || 'Could not save this template. Please try again.');
          }
          setSaveModalField(null);
        }}
        onClose={() => setSaveModalField(null)}
      />
    </div>
  );
}
